// Строка People: подпись (ручка, цвет, имя, счётчик, действия) и тело (линии, отпуска, бары,
// контур посадки). Что ляжет и что записать — решает экран.
import { WriteOnly, useCanWrite } from '@core/auth/access';
import type { KeyboardEvent, PointerEvent } from 'react';
import { EyeOff, TreePalm } from 'lucide-react';
import { mondayOf, spanWeeks, type ISODate } from '@core/dates';
import { NewTaskHereMenu } from '../shared/NewTaskHere';
import { NewTaskGhost, type QuickHandlers } from '../shared/QuickTask';
import type { EntityOption } from '@core/ui/EntitySelect';
import { Menu, type MenuItem } from '@core/ui/Menu';
import { VACATION_STRIP, barGeometry, barStyle, trackTop, type Grid } from '../shared/geometry';
import { GridLines } from '../shared/GridHeader';
import { spotProps } from '../shared/spotlight';
import { DeployFlags, dragDanger, endsAfterDeploy } from '../shared/deployGuard';
import { GhostBar, TaskBar, type BarLook, type BarMarks, type DragKind } from '../shared/TaskBar';
import { ReorderHandle } from '../shared/RowActions';
import type { Task } from '../shared/data';
import type { CardTab } from '../shared/cardEdits';
import type { ResizePreview, TaskDrag } from '../shared/interaction';
import { UNASSIGNED, type PersonRow, type Vacation } from './planner';

export type Preview = { kind: 'move'; rowId: string; start_date: string; end_date: string } | ResizePreview;
export type Drag = TaskDrag<string | null, Preview>; // origin: от кого тащим, null — Unassigned
/** Контур новой задачи в строке человека: неделя под курсором. */
export type PeopleHint = { rowId: string; start_date: ISODate; end_date: ISODate };
/** Быстрая задача на People: проект выбирается в форме, исполнитель — человек строки. */
export type PeopleQuick = QuickHandlers<PeopleHint> & { project: { value: string; options: EntityOption[]; onChange: (id: string) => void } };

// Строка человека: ручка порядка, цвет, имя, счётчик; отпуск и скрытие — в меню по правой кнопке.
export function PersonRowLabel({ row, taskCount, onHandle, onHide, onAddVacation }: { row: PersonRow; taskCount: number; onHandle: (e: PointerEvent<HTMLElement>) => void; onHide: () => void; onAddVacation: () => void }) {
  const canWrite = useCanWrite();
  const menu: MenuItem[] = row.unassigned ? [] : [
    ...(canWrite ? [{ label: 'Add vacation', onSelect: onAddVacation, icon: TreePalm }] : []),
    { label: 'Hide (only for you)', onSelect: onHide, icon: EyeOff },
  ];
  return (
    <Menu items={menu}>
    <div data-person-label={row.id} className="group relative h-full flex items-center gap-1 px-2 py-1 font-medium">
      {!row.unassigned && <WriteOnly><ReorderHandle label={row.name} onPointerDown={onHandle} /></WriteOnly>}
      <span className="inline-block w-3 h-3 rounded-full shrink-0" style={{ background: row.color }} />
      <span className={`truncate ${row.unassigned ? 'text-ink-muted italic' : 'font-bold'}`} title={row.name}>{row.name}</span>
      <span className="text-xs text-gray-500 shrink-0">{taskCount} tasks</span>
    </div>
    </Menu>
  );
}

// Отпуск — тень на весь период поверх баров (не ловит мышь: задачи тянутся как обычно)
// и подпись текстом над барами, по ней двойной клик. Не бар: отпуск не задача.
function VacationShade({ v, grid, onOpen, menu }: { v: Vacation; grid: Grid; onOpen: () => void; menu: MenuItem[] }) {
  const g = barGeometry(v, grid);
  if (!g) return null;
  return (
    <Menu items={menu}>
    <div>
      <div role="button" tabIndex={0} title={`Vacation ${v.start_date} – ${v.end_date}${v.note ? ': ' + v.note : ''}`}
        className="min-h-0 absolute top-0 text-[11px] leading-4 font-semibold px-1 truncate cursor-pointer z-[5] text-gray-800"
        style={{ left: g.left, width: g.width, height: VACATION_STRIP }}
        onDoubleClick={onOpen} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
      >
        🏖 {v.note || 'Vacation'}
      </div>
      {/* штриховка вместо серой плиты: читается как «недоступен», не спорит с барами по цвету */}
      <div className="absolute inset-y-0 pointer-events-none z-[4] rounded-sm border border-dashed border-gray-400/70" aria-hidden
        style={{ left: g.left, width: g.width, background: 'repeating-linear-gradient(135deg, rgba(107,114,128,0.22) 0 5px, rgba(243,244,246,0.55) 5px 11px)' }} />
    </div>
    </Menu>
  );
}

type RowGridProps = {
  row: PersonRow; tasks: Task[]; sub: Map<string, number>; strip: number; previewSlot: number; vacations: Vacation[];
  grid: Grid; todayX: number | null; marks: BarMarks; look: BarLook; drag: Drag | null; groupShift: { ids: Set<string>; days: number } | null;
  byPerson: boolean; projectName: (t: Task) => string;
  onBarPointerDown: (task: Task, person: string | null, kind: DragKind, e: PointerEvent<HTMLElement>) => void;
  onOpen: (t: Task, tab?: CardTab) => void; onKey: (t: Task, person: string | null, e: KeyboardEvent<HTMLElement>) => void; menuFor: (t: Task) => MenuItem[];
  onVacation: (v: Vacation) => void;
  vacationMenu: (v: Vacation) => MenuItem[];
  hint: PeopleHint | null; onHint: (h: PeopleHint | null) => void;
  quick: PeopleQuick | null; onQuickAt: (h: PeopleHint) => void;
  onNewAt: (date: ISODate) => void;            // карточка новой задачи с исполнителем строки
  onVacationAt: (date: ISODate) => void;       // отпуск на неделе под курсором
};

export function PersonRowGrid({ row, tasks, sub, strip, previewSlot, vacations, grid, todayX, marks, look, drag, groupShift, byPerson, projectName, onBarPointerDown, onOpen, onKey, menuFor, onVacation, vacationMenu, hint, onHint, quick, onQuickAt, onNewAt, onVacationAt }: RowGridProps) {
  const personKey = row.unassigned ? null : row.id;
  const { barH } = look;
  return (
    <>
      <GridLines grid={grid} todayX={todayX} days={look.days} holidays={look.holidays} spot={look.spotBox} />
      <NewTaskHereMenu grid={grid} onHint={onHint} onNewAt={onNewAt} onQuickAt={onQuickAt}
        hintOf={(date) => ({ rowId: row.id, ...spanWeeks(mondayOf(date), 1) })}
        extra={(date) => (row.unassigned ? [] : [{ label: 'Add vacation here', onSelect: () => onVacationAt(date), icon: TreePalm }])} />
      {vacations.map((v) => <VacationShade key={v.id} v={v} grid={grid} onOpen={() => onVacation(v)} menu={vacationMenu(v)} />)}
      <DeployFlags tasks={tasks} drag={drag} grid={grid} height={barH} topOf={(t) => strip + trackTop(sub.get(t.id) ?? 0, barH)} />
      {tasks.map((t) => {
        const box = barGeometry(t, grid);
        if (!box) return null;
        const isDragged = drag?.task.id === t.id && drag.origin === personKey;
        const shifted = !isDragged && !!groupShift?.ids.has(t.id);
        const style = barStyle(box, { dragged: isDragged, kind: drag?.kind, dx: drag?.dx, previewBox: drag?.preview?.kind === 'resize' ? barGeometry(drag.preview, grid) : null, shiftDays: shifted ? groupShift!.days : null });
        return (
          <TaskBar
            key={t.id} dataIn={personKey ?? UNASSIGNED}
            task={t} box={{ ...box, ...style }} top={strip + trackTop(sub.get(t.id) ?? 0, barH)} height={barH} wrap={look.wrap}
            colors={byPerson && !row.unassigned ? [row.color] : (marks.colors.get(t.id) ?? [])} names={[projectName(t), ...(marks.names.get(t.id) ?? [])]}
            attention={marks.attention.get(t.id)} pending={marks.pendingIds.has(t.id)}
            ghost={(isDragged && drag?.kind === 'move') || shifted} selected={marks.selected.has(t.id)}
            {...spotProps(t, look.spot)}
            danger={dragDanger(t, isDragged, drag, shifted, groupShift)}
            onPointerDown={(kind, e) => onBarPointerDown(t, personKey, kind, e)}
            onOpen={() => onOpen(t)} onOpenQuestions={() => onOpen(t, 'questions')} onKey={(e) => onKey(t, personKey, e)} menu={menuFor(t)} questions={marks.questions.get(t.id)} badges={look.badges} releaseMarks={look.releaseMarks}
          />
        );
      })}
      <NewTaskGhost hint={hint} quick={quick} grid={grid} place={() => ({ top: strip + trackTop(previewSlot, barH), height: barH })}
        form={quick ? { defaultAssignees: row.unassigned ? [] : [row.id], project: quick.project } : undefined} />
      {drag?.preview?.kind === 'move' && drag.preview.rowId === row.id && (() => {
        const g = barGeometry(drag.preview, grid);
        if (!g) return null;
        return <GhostBar left={g.left} width={g.width} top={strip + trackTop(previewSlot, barH)} height={barH} danger={endsAfterDeploy(drag.task, drag.preview.end_date)}>{drag.task.title}</GhostBar>;
      })()}
    </>
  );
}
